import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useScreenAwake } from '../components/MobileCameraRecorder/useScreenAwake';

const WAKE_LOCK_ROUTES = new Set([
  'mobile-camera',
  'room-scan',
  'verification',
  'manual-verification',
  'complete-profile'
]);

/**
 * Matches any path segment against the wake lock route list so nested and
 * language-prefixed paths (e.g. /ENG/mobile-camera/:token) are covered.
 */
const isWakeLockRoute = (pathname: string): boolean =>
  pathname
    .split('/')
    .filter(Boolean)
    .some((segment) => WAKE_LOCK_ROUTES.has(segment));

/**
 * Keeps the screen awake on proctoring / pre-test setup routes so the second
 * camera stream and SSE connection do not drop when the phone sleeps. The wake
 * lock is re-acquired automatically when the tab returns to foreground.
 */
export const useKeepScreenAwakeOnMobile = (): void => {
  const { pathname } = useLocation();
  const { isWakeLockSupported, setWakeLockEnabled } = useScreenAwake();
  const shouldKeepScreenAwake = useMemo(() => isWakeLockRoute(pathname), [pathname]);

  useEffect(() => {
    if (isWakeLockSupported) setWakeLockEnabled(shouldKeepScreenAwake);
  }, [shouldKeepScreenAwake, isWakeLockSupported, setWakeLockEnabled]);
};
